import { GROUND_LEVEL_TYPE, GROUND_LEVEL_USAGE, WORK_AGE, WORK_CATEGORY } from './work.constants';

function normalize(values) {
  return {
    workCategory: values.workCategory,
    workAge: values.workAge,

    hasBasement: values.hasBasement === true,
    basementCount: Number(values.basementCount || 0),

    groundLevelType: values.groundLevelType,

    singleGroundUsage: values.singleGroundUsage,
    singleHasMezzanine: values.singleHasMezzanine === true,

    lowerGroundUsage: values.lowerGroundUsage,
    lowerGroundHasMezzanine: values.lowerGroundHasMezzanine === true,
    lowerGroundRequiresColumnFooting: values.lowerGroundRequiresColumnFooting === true,

    upperGroundUsage: values.upperGroundUsage,
    upperGroundHasMezzanine: values.upperGroundHasMezzanine === true,

    upperFloorsCount: Number(values.upperFloorsCount || 0),

    /**
     * "Work Completed Upto" on modification/extension/addition jobs: floors 1 to this one are
     * already standing, and the new work starts on the floor above. Blank is not yet answered.
     */
    startingFloorNo: completedUpto(values) == null ? 0 : completedUpto(values) + 1,
    existingFloorsCount: completedUpto(values) ?? 0,

    /**
     * Staircase cap, overhead water tank and lift machine room. Each is its own slab, so
     * this is a count rather than a flag; answering "no" is zero of them.
     */
    staircaseOhwtCount:
      values.hasStaircaseOhwt === false ? 0 : Number(values.staircaseOhwtCount || 0),

    slabsInput:
      values.slabsCount === '' || values.slabsCount == null ? null : Number(values.slabsCount),
  };
}

/** "Work Completed Upto" as a whole number, or null while it is blank. */
export function completedUpto(values): number | null {
  const raw = values?.workCompletedUpto;
  if (raw === '' || raw == null) return null;
  const parsed = Math.floor(Number(raw));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function calcColumnFooting({ workCategory, workAge }) {
  if (workCategory === WORK_CATEGORY.ALLIED) return 2;
  if (workAge === WORK_AGE.NEW) return 2;
  return 0;
}

function calcBasement(v) {
  if (v.workCategory === WORK_CATEGORY.ALLIED) return 0;
  if (v.hasBasement) return v.basementCount;
  return v.lowerGroundRequiresColumnFooting ? 1 : 0;
}

function groundCount(usage, hasMezzanine) {
  if (!usage) return 0;
  if (usage === GROUND_LEVEL_USAGE.PARKING) return 1;
  return hasMezzanine ? 2 : 1;
}

function calcGround(v) {
  if (v.workCategory === WORK_CATEGORY.ALLIED) return 0;

  if (v.groundLevelType === GROUND_LEVEL_TYPE.SINGLE) {
    return groundCount(v.singleGroundUsage, v.singleHasMezzanine);
  }

  if (v.groundLevelType === GROUND_LEVEL_TYPE.MULTIPLE) {
    return (
      groundCount(v.lowerGroundUsage, v.lowerGroundHasMezzanine) +
      groundCount(v.upperGroundUsage, v.upperGroundHasMezzanine)
    );
  }

  return 0;
}

function calcUpperFloors(v) {
  if (v.workCategory === WORK_CATEGORY.ALLIED) return 0;
  return v.upperFloorsCount;
}

function calcExistingFloors(v) {
  if (v.workAge !== WORK_AGE.EXISTING) return 0;
  return v.existingFloorsCount;
}

/**
 * The roof, plus whatever sits on it.
 *
 * This used to be a flat "+2 - roof + OHT", which charged every job for an overhead tank
 * whether it had one or not, and charged a job with a staircase cap *and* a tank *and* a
 * lift machine room for a single slab. The roof keeps its own slab; the rest are counted
 * from what the Work Details screen was told.
 */
function calcRoofSlabs(v) {
  return 1 + v.staircaseOhwtCount;
}

/**
 * A building's slabs are simply the floors counted above: basements, ground level, upper
 * floors, the roof and whatever sits on it.
 *
 * This once carried a flat "-2" correction, asked for when jobs read S1-S8 against an
 * expected S1-S6. The firm has since asked for those two slabs back, so the count is the
 * plain total again.
 */
function calcMinSlabs({ workCategory, basementDrawings, groundLevelDrawings, upperFloorDrawings, roofSlabs }) {
  if (workCategory === WORK_CATEGORY.ALLIED) return 2;
  return basementDrawings + groundLevelDrawings + upperFloorDrawings + roofSlabs;
}

function calcSlabsCount(minSlabs, slabsInput) {
  if (slabsInput == null) return minSlabs;
  return Math.max(slabsInput, minSlabs);
}

function range(prefix, start, end) {
  if (end <= 0) return '';
  if (start === end) return `${prefix}${start}`;
  return `${prefix}${start}-${prefix}${end}`;
}

function columnSuffix(count) {
  return range('C', 1, count);
}

function slabSuffix(v, slabsCount, totals) {
  if (slabsCount === 0) return '';

  if (v.workCategory === WORK_CATEGORY.ALLIED || v.workAge === WORK_AGE.NEW) {
    return range('S', 1, slabsCount);
  }

  const primary =
    totals.basementDrawings +
    totals.groundLevelDrawings +
    totals.upperFloorDrawings +
    totals.roofSlabs;

  const newDrawings =
    Math.max(totals.upperFloorDrawings - totals.existingFloorDrawings, 0) + totals.roofSlabs;

  // Never before the first slab, and never past the last one being numbered.
  const start = Math.min(Math.max(primary - newDrawings + 1, 1), slabsCount);

  return range('S', start, slabsCount);
}

export function calculateDrawings(values = {}) {
  const v = normalize(values);

  const columnFootingDrawings = calcColumnFooting(v);
  const basementDrawings = calcBasement(v);
  const groundLevelDrawings = calcGround(v);
  const upperFloorDrawings = calcUpperFloors(v);
  const existingFloorDrawings = calcExistingFloors(v);

  const roofSlabs = calcRoofSlabs(v);

  const minSlabs = calcMinSlabs({
    workCategory: v.workCategory,
    basementDrawings,
    groundLevelDrawings,
    upperFloorDrawings,
    roofSlabs,
  });

  const slabsCount = calcSlabsCount(minSlabs, v.slabsInput);

  const structuralDrawings = columnFootingDrawings + slabsCount;

  return {
    columnFootingDrawings,
    basementDrawings,
    groundLevelDrawings,
    upperFloorDrawings,
    existingFloorDrawings,

    minSlabs,
    slabsCount,

    /** Slabs on the roof: the roof itself plus each staircase cap / OHWT / LMR. */
    roofSlabs,
    staircaseOhwtSlabs: v.staircaseOhwtCount,
    /** The floor the new work starts at; 0 when the job is not existing work. */
    startingFloorNo: v.workAge === WORK_AGE.EXISTING ? v.startingFloorNo : 0,

    columnRange: columnSuffix(columnFootingDrawings),
    slabRange: slabSuffix(v, slabsCount, {
      basementDrawings,
      groundLevelDrawings,
      upperFloorDrawings,
      existingFloorDrawings,
      roofSlabs,
    }),

    structuralDrawings,
  };
}
